import { fetchRecordingAgendaItems } from "@/hooks/useRecordingAgendaItems";
import { AgendaItemDetails, AttendeeListItem } from "@/models/prepareRecording";
import { Recording, RecordingCreationType, RecordingUUIDUser, Subject } from "@/models/recording";
import { formatAgendaDate } from "@/utils/formatAgendaDate";
import { getAttendanceInitials } from "@/utils/getAttendanceInitials";
import { resolveRecordingLanguage } from "@/utils/helpers/recording";
import { TFunction } from "i18next";

export interface RecordingSetupParams {
  selectedAgendaId: string;
  agendasList: AgendaItemDetails[];
  selectedAttendees: AttendeeListItem[];
  selectedLanguage: string;
  lang: string | undefined;
  deviceKey: string;
  createNewRecording: (params: {
    agendaId: string;
    recordingUUIDUsers: RecordingUUIDUser[];
    language: string;
    recordingType?: RecordingCreationType;
  }) => Promise<string>;
  t: TFunction;
  recordingType?: RecordingCreationType;
}

export interface RecordingSetupData {
  recordingUUID: string;
  language: string;
  meetingId: string;
  meetingName: string;
  meetingDateTime: string;
  meetingStartDate: string;
  meetingEndDate: string;
  guestsList: string[];
  guestsListInitials: string[];
  guestsListFullName: string[];
  subjects: Subject[];
}

// Shared by createNewRecordingHandler (live mic recording) and uploadRecordingHandler (file
// upload): both need a fresh recordingUUID scoped to the same agenda/attendees/language, plus
// the same meeting metadata for the local RecordingData row. What happens next (mic capture vs.
// uploading files) is specific to each caller.
export async function buildRecordingSetupData(params: RecordingSetupParams): Promise<RecordingSetupData> {
  const { selectedAgendaId, agendasList, selectedAttendees, selectedLanguage, lang, deviceKey, createNewRecording, t, recordingType } = params;

  const language = resolveRecordingLanguage(selectedLanguage, lang);

  const recordingUUIDUsers: RecordingUUIDUser[] = selectedAttendees.map((attendee) => ({
    ibabsUserId: attendee.id,
    firstName: attendee.name,
    lastName: attendee.lastName || "",
  }));

  const recordingUUID = await createNewRecording({ agendaId: selectedAgendaId, recordingUUIDUsers, language, recordingType });

  const subjects = await fetchRecordingAgendaItems(deviceKey, recordingUUID);

  const selectedAgendaData = agendasList.find((agenda) => agenda.id === selectedAgendaId);

  return {
    recordingUUID,
    language,
    meetingId: selectedAgendaId,
    meetingName: selectedAgendaData?.agendaType || "",
    meetingDateTime: formatAgendaDate(selectedAgendaData?.startDate, selectedAgendaData?.endDate, null, null, t),
    meetingStartDate: selectedAgendaData?.startDate || "",
    meetingEndDate: selectedAgendaData?.endDate || "",
    guestsList: selectedAttendees.map((attendee) => attendee.id),
    guestsListInitials: selectedAttendees.map((attendee) => attendee.initials),
    guestsListFullName: selectedAttendees.map((attendee) => attendee.name),
    subjects: subjects || [],
  };
}

// Used by uploadRecordingHandler when retrying a failed upload: reuses the existing recording
// (its agenda/attendees/language were already set when it was first created and aren't being
// changed) instead of creating a new one, deriving everything from the already-fetched Recording
// rather than from local storage.
export async function buildRetryRecordingSetupData(recording: Recording, deviceKey: string): Promise<RecordingSetupData> {
  const subjects = await fetchRecordingAgendaItems(deviceKey, recording.recordingId);
  const users = recording.users ?? [];

  return {
    recordingUUID: recording.recordingId,
    language: "",
    meetingId: recording.agendaId,
    meetingName: recording.title,
    meetingDateTime: recording.date,
    meetingStartDate: recording.date,
    meetingEndDate: recording.date,
    guestsList: users.map((user) => user.userId),
    guestsListInitials: users.map((user) => getAttendanceInitials(user.firstName) || ""),
    guestsListFullName: users.map((user) => user.firstName),
    subjects: subjects || [],
  };
}
